import { contentfulClient } from "../lib/contentful-client";

/**
 * Query key factory for protocol-related queries
 */
export const protocolQueryKeys = {
  all: ["protocols"] as const,
  lists: () => [...protocolQueryKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) => [...protocolQueryKeys.lists(), filters] as const,
};

/**
 * Simplified protocol type for serializable data
 */
export interface SerializableProtocol {
  id: string;
  name: string;
  description: string;
  symbol: string;
  logo: {
    url: string;
    title?: string;
    description?: string;
  };
  createdAt: string;
  updatedAt: string;
}

/**
 * Fetch raw protocol entries from Contentful
 */
export async function fetchProtocols() {
  try {
    return await contentfulClient.getEntries({
      content_type: "protocol",
    });
  } catch (error) {
    console.error("Error fetching protocols from Contentful:", error);
    throw error;
  }
}

/**
 * Shared query configuration for protocol data
 * Used by both the hook and global loader to ensure consistency
 */
export const protocolQueryConfig = {
  staleTime: 1000 * 60 * 10, // 10 minutes - protocols don't change frequently
  gcTime: 1000 * 60 * 30, // 30 minutes - keep in cache longer
  // Disable structural sharing to prevent circular reference issues with Contentful data
  structuralSharing: false,
} as const;

export enum ProtocolKey {
  AAVE_V2 = "aave-v2",
  AAVE_V3 = "aave-v3",
  AAVE_V3_TESTNET = "aave-v3-testnet",
  COMPOUND_V2 = "compound-v2",
  COMPOUND_V3 = "compound-v3",
  MORPHO = "morpho",
}
export interface LendBorrowProject {
  id: string;
  key: ProtocolKey;
  name: string;
  chainId: string;
  logoUrl: string;
}

// TODO: remove this once we have a proper logo in sdk
const aaveLogo =
  "https://images.ctfassets.net/hwqnbx905qdp/1mtpezUbEfFb77N0L066hw/eda58c468a7959398f015d7c9e545d46/Aave_Logo.svg";
// Lending protocol IDs and chain IDs from Expand Network docs
// Chain IDs use CAIP-2 format per https://docs.portalhq.io/resources/chain-id-formatting
const LENDBORROW_PROJECTS: LendBorrowProject[] = [
  // Ethereum Mainnet
  // {
  //   id: "1000",
  //   key: ProtocolKey.AAVE_V2,
  //   name: "Aave V2",
  //   chainId: "eip155:1",
  //   logoUrl: aaveLogo,
  // },
  {
    id: "1200",
    key: ProtocolKey.AAVE_V3,
    name: "Aave V3",
    chainId: "eip155:1",
    logoUrl: aaveLogo,
  },
  // TODO JP: Compound requires assets as parameters to get pools. We need to see if we can change this on the API side.
  // {
  //   id: "1100",
  //   name: "Compound V2",
  //   chainId: "eip155:1",
  // },
  // {
  //   id: "1300",
  //   name: "Compound V3",
  //   chainId: "eip155:1",
  // },

  // TODO JP: Morpho has a different response schema. We need to see if we can change this on the API side.
  // {
  //   id: "1400",
  //   name: "Morpho",
  //   chainId: "eip155:1",
  // },

  // Ethereum Testnet Sepolia
  {
    id: "1202",
    key: ProtocolKey.AAVE_V3_TESTNET,
    name: "Aave V3 Testnet",
    chainId: "eip155:11155111",
    logoUrl: aaveLogo,
  },
  // {
  //   id: "1302",
  //   name: "Compound V3 Testnet",
  //   chainId: "eip155:11155111",
  // },

  // Avalanche
  // {
  //   id: "1003",
  //   name: "Aave V2",
  //   key: ProtocolKey.AAVE_V2,
  //   chainId: "eip155:43114",
  //   logoUrl: aaveLogo,
  // },
  // {
  //   id: "1203",
  //   name: "Aave V3",
  //   key: ProtocolKey.AAVE_V3,
  //   chainId: "eip155:43114",
  //   logoUrl: aaveLogo,
  // },

  // Polygon
  // {
  //   id: "1005",
  //   name: "Aave V2",
  //   key: ProtocolKey.AAVE_V2,
  //   chainId: "eip155:137",
  //   logoUrl: aaveLogo,
  // },
  // {
  //   id: "1205",
  //   name: "Aave V3",
  //   key: ProtocolKey.AAVE_V3,
  //   chainId: "eip155:137",
  //   logoUrl: aaveLogo,
  // },

  // Arbitrum
  {
    id: "1204",
    name: "Aave V3",
    key: ProtocolKey.AAVE_V3,
    chainId: "eip155:42161",
    logoUrl: aaveLogo,
  },

  // Base
  {
    id: "1206",
    name: "Aave V3",
    key: ProtocolKey.AAVE_V3,
    chainId: "eip155:8453",
    logoUrl: aaveLogo,
  },
  // {
  //   id: "1402",
  //   name: "Morpho",
  //   chainId: "eip155:8453",
  // },

  // Optimism
  {
    id: "1207",
    name: "Aave V3",
    key: ProtocolKey.AAVE_V3,
    chainId: "eip155:10",
    logoUrl: aaveLogo,
  },
] as const;

/**
 * Get all available lending/borrowing protocols for a specific chain
 */
export function getProtocolsForChain(chainId: string) {
  return LENDBORROW_PROJECTS.filter(
    (project) => project.chainId.toLowerCase() === chainId.toLowerCase(),
  );
}

export function getProtocolById(id: string) {
  return LENDBORROW_PROJECTS.find((project) => project.id === id);
}

export function getProtocolsByKey(key: ProtocolKey) {
  return LENDBORROW_PROJECTS.filter((project) => project.key === key);
}

export function getAllSupportedProtocols(includeTestnets = false) {
  return LENDBORROW_PROJECTS.filter(
    (project) => includeTestnets || project.key !== ProtocolKey.AAVE_V3_TESTNET,
  );
}
